import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Trophy, TrendingUp, TrendingDown, Target, CheckCircle2, AlertCircle, 
  Edit3, Save, X, Calendar, Clock, DollarSign 
} from "lucide-react";

const GoalsView = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [deltaInfo, setDeltaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("year");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Editable fields
  const [editData, setEditData] = useState({
    revenueGoal: 0,
    currentRevenue: 0,
    goalEndDate: ""
  });

  useEffect(() => {
    fetchData();
  }, [userId]);

  useEffect(() => {
    if (user) {
      setEditData({
        revenueGoal: user.revenueGoal || 0,
        currentRevenue: user.currentRevenue || 0,
        goalEndDate: user.goalEndDate || ""
      });
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [userRes, deltaRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`),
        axios.get(`${API}/agent/goal-delta/${userId}`)
      ]);
      setUser(userRes.data);
      setDeltaInfo(deltaRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load goals");
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${userId}`, {
        revenueGoal: parseFloat(editData.revenueGoal),
        currentRevenue: parseFloat(editData.currentRevenue),
        goalEndDate: editData.goalEndDate
      });
      
      // Refresh data
      await fetchData();
      setIsEditing(false);
      toast.success("Goals updated successfully!");
    } catch (error) {
      console.error("Error saving goals:", error);
      toast.error("Failed to save goals");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      revenueGoal: user.revenueGoal || 0,
      currentRevenue: user.currentRevenue || 0,
      goalEndDate: user.goalEndDate || ""
    });
    setIsEditing(false);
  };

  const calculateDaysRemaining = () => {
    if (!editData.goalEndDate) return null;
    const endDate = new Date(editData.goalEndDate);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading || !user || !deltaInfo) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-cyan-500"></div>
      </div>
    );
  }

  const progressPercentage = deltaInfo.progressPercentage;
  const currentRevenue = user.currentRevenue;
  const revenueGoal = user.revenueGoal;
  const remaining = revenueGoal - currentRevenue;
  const daysRemaining = calculateDaysRemaining();

  const getPacingStatus = () => {
    if (progressPercentage >= 90) return { status: "Ahead", icon: TrendingUp, color: "text-green-400", bgColor: "bg-green-500/10" };
    if (progressPercentage >= 60) return { status: "On Pace", icon: Target, color: "text-cyan-400/80", bgColor: "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30/10" };
    return { status: "Behind", icon: TrendingDown, color: "text-red-400", bgColor: "bg-red-500/10" };
  };

  const pacing = getPacingStatus();
  const PacingIcon = pacing.icon;

  const milestones = [
    { value: revenueGoal * 0.25, label: "25% Milestone", achieved: currentRevenue >= revenueGoal * 0.25 },
    { value: revenueGoal * 0.5, label: "50% Milestone", achieved: currentRevenue >= revenueGoal * 0.5 },
    { value: revenueGoal * 0.75, label: "75% Milestone", achieved: currentRevenue >= revenueGoal * 0.75 },
    { value: revenueGoal, label: "100% Goal", achieved: currentRevenue >= revenueGoal }
  ];

  const activityMetrics = [
    { label: "Accounts Engaged", value: 12, peer: 15, status: "below" },
    { label: "Deals Created", value: 8, peer: 10, status: "below" },
    { label: "Emails Sent", value: 145, peer: 120, status: "above" },
    { label: "Meetings Held", value: 18, peer: 20, status: "below" }
  ];

  return (
    <div className="space-y-6" data-testid="goals-view">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-normal text-white mb-2 flex items-center gap-2">
            <Trophy className="h-8 w-8 text-neon-cyan" />
            <span className="text-neon-cyan">Goals & Progress</span>
          </h1>
          <p className="text-gray-400">Track your performance and milestones</p>
        </div>
        
        {/* Edit Button */}
        {!isEditing ? (
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-white/10"
            data-testid="edit-goals-btn"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Edit Goals
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              onClick={handleCancel}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-white/10"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 text-white"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Time Period Selector */}
      <div className="flex gap-2">
        {["month", "quarter", "year"].map((period) => (
          <Button
            key={period}
            onClick={() => setTimePeriod(period)}
            variant={timePeriod === period ? "default" : "outline"}
            className={timePeriod === period
              ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 text-white"
              : "border-gray-700 text-gray-400 hover:bg-white/5"
            }
            data-testid={`period-${period}`}
          >
            {period.charAt(0).toUpperCase() + period.slice(1)}
          </Button>
        ))}
      </div>

      {/* Main Progress Card */}
      <Card className="glass-card border-gray-800/50 p-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Revenue Goal</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <Input
                      type="number"
                      value={editData.revenueGoal}
                      onChange={(e) => setEditData(prev => ({ ...prev, revenueGoal: e.target.value }))}
                      className="pl-10 bg-white/5 border-gray-700 text-white text-2xl font-normal w-64"
                      data-testid="revenue-goal-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Current Revenue</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <Input
                      type="number"
                      value={editData.currentRevenue}
                      onChange={(e) => setEditData(prev => ({ ...prev, currentRevenue: e.target.value }))}
                      className="pl-10 bg-white/5 border-gray-700 text-white text-xl w-64"
                      data-testid="current-revenue-input"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-normal text-white mb-2">
                  ${(revenueGoal / 1000000).toFixed(1)}M
                </h2>
                <p className="text-gray-400">Revenue Goal</p>
              </>
            )}
          </div>
          <Badge className={`${pacing.color} border-0 bg-white/10 text-lg px-4 py-2 flex items-center gap-2`}>
            <PacingIcon className="h-5 w-5" />
            {pacing.status}
          </Badge>
        </div>

        {/* Goal End Date - Critical for teleological system */}
        <div className={`mb-8 p-4 rounded-lg border ${daysRemaining && daysRemaining < 30 ? 'bg-red-500/10 border-red-500/30' : 'bg-white/5 border-gray-700'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className={`h-5 w-5 ${daysRemaining && daysRemaining < 30 ? 'text-red-400' : 'text-cyan-400/80'}`} />
              <div>
                <p className="text-sm text-gray-400">Goal Completion Date</p>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editData.goalEndDate}
                    onChange={(e) => setEditData(prev => ({ ...prev, goalEndDate: e.target.value }))}
                    className="bg-white/5 border-gray-700 text-white mt-1 w-48"
                    data-testid="goal-end-date-input"
                  />
                ) : (
                  <p className="text-lg font-normal text-white">{formatDate(user.goalEndDate)}</p>
                )}
              </div>
            </div>
            {daysRemaining !== null && !isEditing && (
              <div className="text-right">
                <div className={`flex items-center gap-2 ${daysRemaining < 30 ? 'text-red-400' : daysRemaining < 90 ? 'text-yellow-400' : 'text-cyan-400/80'}`}>
                  <Clock className="h-4 w-4" />
                  <span className="text-2xl font-normal">{daysRemaining}</span>
                </div>
                <p className="text-sm text-gray-400">days remaining</p>
              </div>
            )}
          </div>
          {daysRemaining !== null && daysRemaining > 0 && !isEditing && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Required run rate: <span className="text-white font-normal">${((remaining / daysRemaining) * 30 / 1000).toFixed(0)}K/month</span> to hit goal
              </p>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Current Progress</span>
            <span className="text-lg font-normal text-white">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-6 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#3dd9c5] to-[#2dd4bf] transition-all duration-500 flex items-center justify-end px-2"
              style={{ width: `${Math.min(100, progressPercentage)}%` }}
            >
              {progressPercentage > 10 && (
                <span className="text-xs font-normal text-white">
                  ${(currentRevenue / 1000).toFixed(0)}K
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-400 mb-1">Current Revenue</p>
            <p className="text-2xl font-normal text-white">${(currentRevenue / 1000).toFixed(0)}K</p>
          </div>
          <div>
            <p className="text-sm text-gray-400 mb-1">Remaining to Goal</p>
            <p className="text-2xl font-normal text-cyan-400/80">${(remaining / 1000).toFixed(0)}K</p>
          </div>
          <div>
            <p className="text-sm text-gray-400 mb-1">Pipeline Value</p>
            <p className="text-2xl font-normal text-yellow-400">$343K</p>
          </div>
        </div>
      </Card>

      {/* Milestones */}
      <Card className="glass-card border-gray-800/50 p-6">
        <h3 className="text-xl font-normal text-white mb-6">Milestones</h3>
        <div className="space-y-4">
          {milestones.map((milestone, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                milestone.achieved
                  ? "bg-green-500/10 border-green-500/30"
                  : "bg-white/5 border-gray-700/50"
              }`}
              data-testid={`milestone-${idx}`}
            >
              <div className="flex items-center gap-3">
                {milestone.achieved ? (
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-gray-500" />
                )}
                <div>
                  <p className="font-normal text-white">{milestone.label}</p>
                  <p className="text-sm text-gray-400">${(milestone.value / 1000).toFixed(0)}K</p>
                </div>
              </div>
              {milestone.achieved && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                  Achieved
                </Badge>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Activity Metrics vs Peers */}
      <Card className="glass-card border-gray-800/50 p-6">
        <h3 className="text-xl font-normal text-white mb-6">Activity Metrics vs Peers</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activityMetrics.map((metric, idx) => (
            <div
              key={idx}
              className="p-4 bg-white/5 border border-gray-700/50 rounded-lg"
              data-testid={`metric-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-400">{metric.label}</p>
                <Badge className={`${
                  metric.status === "above"
                    ? "bg-green-500/20 text-green-400 border-green-500/50"
                    : "bg-red-500/20 text-red-400 border-red-500/50"
                } text-xs`}>
                  {metric.status === "above" ? "Above" : "Below"} Avg
                </Badge>
              </div>
              <div className="flex items-end gap-4">
                <div>
                  <p className="text-xs text-gray-500">You</p>
                  <p className="text-2xl font-normal text-white">{metric.value}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Peer Avg</p>
                  <p className="text-xl font-normal text-gray-400">{metric.peer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Recommended Actions */}
      {pacing.status === "Behind" && (
        <Card className="glass-card border-red-500/30 p-6">
          <h3 className="text-xl font-normal text-white mb-4 flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-400" />
            Recommended Actions to Get Back on Pace
          </h3>
          <div className="space-y-3">
            {deltaInfo.recommendedActions.map((action, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="p-1 bg-red-500/20 rounded mt-0.5">
                  <Target className="h-4 w-4 text-red-400" />
                </div>
                <p className="text-sm text-gray-300 flex-1">{action}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default GoalsView;
